import type { MouseEvent } from 'react';
import {
  EMPTY_LESSON_SUBJECT,
  type DiaryEntry,
  type DiaryEvaluation,
  type DiaryEvaluationResponse,
  type DiaryLesson,
  type DiaryTopic,
  type PreschoolEducationPlan,
} from '@/types/diary';
import { doPeriodsOverlap, isWeekend, parseLocalDate } from '@/utils/date';
import { stripHtml } from '@/utils/text';

export const toDiaryEvaluation = ({
  id,
  value,
  nonAttendanceReason,
  isNonAttendance,
  isWeighted,
  editedEvaluation,
  lesson,
}: DiaryEvaluationResponse): DiaryEvaluation => ({
  id,
  value,
  previousValue: editedEvaluation?.value,
  lessonTypeId: lesson.type.lessonTypeId,
  lessonTypeName: lesson.type.name,
  nonAttendanceType: nonAttendanceReason ?? null,
  isNonAttendance,
  isWeighted,
  isTest: lesson.isTest,
});

export const hasDiaryContent = (entry: DiaryEntry): boolean =>
  !!entry.lessons.length ||
  !!entry.behaviourEntries.length ||
  !!entry.notes.length;

export const countChangedLessons = (lessons: DiaryLesson[]): number =>
  lessons.filter(({ isOnlyForCurrentWeek }) => isOnlyForCurrentWeek).length;

export const isHiddenEmptyWeekend = (entry: DiaryEntry): boolean =>
  isWeekend(parseLocalDate(entry.date)) && !hasDiaryContent(entry);

interface PlansInPeriodParams {
  plans: PreschoolEducationPlan[];
  from: string;
  to: string;
}

export const getPlansInPeriod = ({
  plans,
  from,
  to,
}: PlansInPeriodParams): PreschoolEducationPlan[] =>
  plans.filter((plan) =>
    doPeriodsOverlap({ from, to }, { from: plan.dateFrom, to: plan.dateTo }),
  );

export const getTopicTexts = (lesson: DiaryLesson): string[] =>
  lesson.topics.map((topic) => stripHtml(topic.text)).filter(Boolean);

interface HomeworkTextsParams {
  lesson: DiaryLesson;
  emptyLabel: string;
  deletedLabel: string;
}

export const getHomeworkTexts = ({
  lesson,
  emptyLabel,
  deletedLabel,
}: HomeworkTextsParams): string[] => [
  ...lesson.homeTasks.map((task) => stripHtml(task.text) || emptyLabel),
  ...lesson.thirdPartyEvents.map((event) => {
    if (event.isDeleted) {
      return deletedLabel;
    }

    return stripHtml(event.name) || emptyLabel;
  }),
];

export const hasEmptySubject = (lesson: DiaryLesson): boolean =>
  lesson.className === EMPTY_LESSON_SUBJECT;

export const shouldIgnoreLessonClick = (event: MouseEvent<HTMLElement>) => {
  const target = event.target as Element;
  const isFromPortal = !event.currentTarget.contains(target);
  const isInnerControl = !!target.closest('a, button');
  const isSelectingText = !!window.getSelection()?.toString();

  return isFromPortal || isInnerControl || isSelectingText;
};

export const hasDraftTopic = (topics: DiaryTopic[]): boolean =>
  topics.some(({ isDraft }) => isDraft);
